import api from './api';

export type MaterialApprovalStatus =
  | 'Przygotowane'
  | 'Wysłane'
  | 'Zatwierdzone bez uwag'
  | 'Zatwierdzone z uwagami'
  | 'Odrzucone';

export type MaterialApprovalBranch = 'Budowlana' | 'Sanitarna' | 'Elektryczna' | 'Teletechniczna';

export interface MaterialApprovalDto {
  id: string | number;
  kzm_id?: string;
  prepared_at: string; // ISO YYYY-MM-DD or ISO datetime
  sent_at?: string | null;
  // Statusy rozdzielone na status karty i status zatwierdzenia
  card_status?: 'Przygotowana' | 'Sprawdzona' | 'Wysłana' | 'Do poprawienia';
  card_status_at?: string | null;
  approval_status?: 'Akceptacja bez uwag' | 'Akceptacja z uwagami' | 'Brak akceptacji' | '';
  approval_status_at?: string | null; // = client_decision_date
  // Dla wstecznej kompatybilności (jeśli backend jeszcze zwraca stare pola)
  status?: MaterialApprovalStatus;
  status_at?: string | null;
  project_id: string;
  project_name?: string;
  branch: MaterialApprovalBranch;
  material_name: string; // Opis
  prepared_by_user_id?: string;
  prepared_by_name?: string;
  prepared_by_avatar_url?: string;
  google_drive_file_url?: string;
}

export async function listMaterialApprovals(projectId?: string): Promise<MaterialApprovalDto[]> {
  try {
    // Backend KZM
    const url = projectId ? `/kzms/project/${encodeURIComponent(projectId)}` : '/kzms';
    const { data } = await api.get(url);
    const raw = Array.isArray(data) ? data : (data?.data ?? []);
    // Mapuj pola backendu -> frontend
    return (raw || []).map((r: any) => ({
      id: r.id ?? r.ID ?? r.kzm_id ?? r.kzmId,
      kzm_id: r.kzm_id || r.kzmId || undefined,
      prepared_at: (r.created_at || r.prepared_at || r.submission_date || '').slice(0, 10),
      sent_at: r.sent_at || null,
      card_status: r.card_status || r.status || undefined,
      card_status_at: r.card_status_at || r.status_at || null,
      approval_status: r.client_decision || r.approval_status || '',
      approval_status_at: r.client_decision_date || r.approval_status_at || null,
      project_id: String(r.project_id || ''),
      project_name: r.project?.project_name || r.project_name || '',
      branch: r.material_branch || r.branch,
      material_name: r.material_trade_name || r.material_name || r.install_place || '',
      prepared_by_user_id: r.created_by_user_id,
      prepared_by_name: r.prepared_by_user_name || '',
      prepared_by_avatar_url: r.prepared_by_avatar_url || '',
      google_drive_file_url: r.drive_folder_id ? `https://drive.google.com/drive/folders/${r.drive_folder_id}` : (r.generated_kzm_pdf_url || r.google_drive_file_url || ''),
    } as MaterialApprovalDto));
  } catch (e) {
    return [];
  }
}

export async function sendMaterialApproval(id: string | number): Promise<MaterialApprovalDto> {
  try {
    const { data } = await api.post(`/kzms/${id}/send`);
    return data?.data ?? data;
  } catch (e) {
    // Fallback: lokalna symulacja zmiany statusu
    const now = new Date();
    const iso = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    return {
      id,
      prepared_at: iso,
      sent_at: iso,
      card_status: 'Wysłana',
      card_status_at: iso,
      approval_status: '',
      approval_status_at: null,
      project_id: '',
      branch: 'Budowlana',
      material_name: '',
    } as MaterialApprovalDto;
  }
}

// Tworzenie KZM
export async function createMaterialApproval(payload: Partial<{
  project_id: string;
  material_branch: MaterialApprovalBranch | string;
  material_trade_name: string;
  install_place?: string;
  compliance?: 'Zgodny z projektem' | 'Równoważny' | 'Nie określony w projekcie' | '';
}>): Promise<any> {
  const { data } = await api.post('/kzms', payload);
  return data?.data ?? data;
}

// Upload załącznika Zx
export async function uploadMaterialApprovalAttachment(kzmId: string | number, file: File, description?: string): Promise<any> {
  const form = new FormData();
  form.append('file', file);
  if (description) form.append('file_description', description);
  const { data } = await api.post(`/kzms/${kzmId}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data?.data ?? data;
}

// Szybkie utworzenie folderu KZM i upload załączników (bez zapisu rekordu KZM)
export async function quickCreateKzmUpload(
  params: { project_id: string; material_branch: string; files: File[]; material_trade_name?: string; install_place?: string; submission_date?: string; compliance?: 'Zgodny z projektem' | 'Równoważny' | 'Nie określony w projekcie' | '' },
  googleAccessToken?: string,
  fileDescriptions?: string[]
): Promise<any> {
  const form = new FormData();
  form.append('project_id', params.project_id);
  form.append('material_branch', params.material_branch);
  if (params.material_trade_name) form.append('material_trade_name', params.material_trade_name);
  if (params.install_place !== undefined) form.append('install_place', params.install_place);
  if (params.submission_date) form.append('submission_date', params.submission_date);
  if (params.compliance) form.append('compliance', params.compliance);
  for (const f of params.files) form.append('files', f, f.name);
  // Opcjonalne opisy plików w tej samej kolejności
  (fileDescriptions || []).forEach((d) => form.append('file_descriptions', d ?? ''));
  // Nie ustawiaj ręcznie Content-Type – przeglądarka doda boundary automatycznie
  const { data } = await api.post('/kzm-quick/create', form, { headers: googleAccessToken ? { 'x-google-access-token': googleAccessToken } : undefined });
  return data?.data ?? data;
}

export async function getKzmDetails(kzmId: string): Promise<{ kzm: any; attachments: any[] }> {
  const { data } = await api.get(`/kzms/${encodeURIComponent(kzmId)}`);
  return data?.data ?? { kzm: null, attachments: [] };
}

export async function reviseKzm(
  kzmId: string,
  payload: Partial<{ material_trade_name: string; install_place: string; submission_date: string; compliance: 'Zgodny z projektem'|'Równoważny'|'Nie określony w projekcie'; status: string }>,
  options?: { keepDriveIds?: Array<{ driveId: string; name?: string; description?: string }>; newFiles?: Array<{ file: File; name?: string; description?: string }>; googleAccessToken?: string }
): Promise<{ kzm_id: string; drive_folder_id?: string }> {
  // Jeśli są nowe pliki, użyj multipart/form-data
  if (options && ((options.newFiles && options.newFiles.length) || (options.keepDriveIds && options.keepDriveIds.length))) {
    const form = new FormData();
    form.append('payload', JSON.stringify({ ...payload, keepDriveIds: options.keepDriveIds || [] }));
    for (const nf of options.newFiles || []) {
      form.append('files', nf.file, nf.name || nf.file.name);
      form.append('file_descriptions', nf.description || '');
    }
    const { data } = await api.post(`/kzms/${encodeURIComponent(kzmId)}/revise`, form, { headers: options?.googleAccessToken ? { 'x-google-access-token': options.googleAccessToken } : undefined });
    return data?.data ?? data;
  }
  const { data } = await api.post(`/kzms/${encodeURIComponent(kzmId)}/revise`, payload || {}, { headers: options?.googleAccessToken ? { 'x-google-access-token': options.googleAccessToken } : undefined });
  return data?.data ?? data;
}

export async function deleteKzm(kzmId: string): Promise<{ deleted: boolean; kzm_id: string }> {
  const { data } = await api.delete(`/kzms/${encodeURIComponent(kzmId)}`);
  return data?.data ?? data;
}

export async function sendKzmEmail(
  kzmId: string,
  payload: { from: string; to: string; cc?: string; subject: string; body: string; includeAllAttachments?: boolean }
): Promise<{ sent: boolean }>{
  const { data } = await api.post(`/kzms/${encodeURIComponent(kzmId)}/send-email`, payload);
  return data?.data ?? data;
}


